import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  OnDestroy,
  output,
  signal,
  viewChild,
  viewChildren,
} from "@angular/core";
import { EngagementOption } from "backend/model/engagementOption";
import { EngagementRole } from "backend/model/engagementRole";
import { EngagementState } from "backend/model/engagementState";
import { TeamMemberStatus } from "backend/model/teamMemberStatus";
import { TreeNode } from "primeng/api";
import { faStar } from "@fortawesome/free-regular-svg-icons";
import { faStar as faStarFilled } from "@fortawesome/free-solid-svg-icons";
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from "primeng/dynamicdialog";
import { TreeComponent, TreeModule } from "src/prime/tree/tree";
import {
  TreeNodeCollapseEvent,
  TreeNodeExpandEvent,
} from "src/prime/tree/tree.interface";
import { EngagementDetailsComponent } from "src/app/engagement/engagement-details/engagement-details.component";
import { WorkspaceListService } from "src/app/services/workspace-list.service";
import { FavoriteEngagementWatcherService } from "src/app/workspace/workspace-list/favorite-engagement-watcher.service";
import {
  MenuHoverEvent,
  MenuToggleEvent,
  WorkspaceListTreeItemComponent,
} from "src/app/workspace/workspace-list/workspace-list-tree/workspace-list-tree-item/workspace-list-tree-item.component";
import {
  WorkspaceTreeNode,
  WorkspaceFilterCode,
  APTVersionType,
  APTVersion,
  WorkspaceListGroupCode,
  WorkspaceTreeNodeData,
} from "src/app/workspace/workspace-list/workspace-list.component";
import {
  FocusService,
  isInputFocused,
} from "src/standard/services/focus.service";
import { IHaveChildDialogRefs } from "src/standard/services/pending-changes.service";
import { StandardButtonDropdownActionsComponent } from "src/standard/standard-button-dropdown/standard-button-dropdown-actions/standard-button-dropdown-actions.component";
import { IStandardButtonDropdownItem } from "src/standard/standard-button-dropdown/standard-button-dropdown.component";
import { StandardContextMenuComponent } from "src/standard/standard-context-menu/standard-context-menu.component";
import { StandardCollapseIconComponent } from "src/standard/standard-icon/standard-collapse-icon.component";
import { StandardLineClampComponent } from "src/standard/standard-line-clamp/standard-line-clamp.component";
import { StandardPageBlockingSpinnerComponent } from "src/standard/standard-page-blocking-spinner/standard-page-blocking-spinner.component";
import { StandardSkeletonComponent } from "src/standard/standard-skeleton/standard-skeleton.component";
import { TransitionState } from "src/standard/ts/formUtils";
import { isStrictEqualish } from "src/standard/ts/isStrictEqualish";
import invariant from "tiny-invariant";
import { EngagementTeamMember } from "backend/model/engagementTeamMember";
import { SnapshotManagementService } from "src/app/services/snapshot-management.service";
import { APTContext } from "src/app/services/aptContext/apt-context";
import {
  BlockingConfirmationDialogRequest,
  StandardConfirmationDialogRequest,
} from "src/standard/standard-confirmation-dialog/standard-confirmation-dialog.types";
import { ModalDialogService } from "src/standard/standard-modal-button/showModalDialog";
import { KeyboardShortcutService } from "src/standard/services/keyboard-shortcut.service";
import type { SerializedNode } from "src/prime/tree/utils/serializeNodes";
import { APTCodes } from "src/app/workspace/aptCodes";

const CACHE_ENGAGEMENT_MEMBER_DATA_MS = 60_000 * 5; // 5 minutes

export interface ICopyCloneAndRollforwardModalProps {
  engagementId?: string;
  engagementOption: EngagementOption;
  fromSearchPanel?: boolean;
  engagementMemberFirmId?: number;
}

@Component({
  standalone: true,
  selector: "app-workspace-list-tree",
  templateUrl: "./workspace-list-tree.component.html",
  host: {
    "[class.h-100]": "height() === 100",
  },
  styles: `
    :host {
      position: relative;
      display: block;
    }

    :host ::ng-deep {
      .flat-tree {
        .p-treenode-children {
          padding-inline-start: 0px !important;
        }

        .p-treenode-selectable {
          padding-inline-start: 2rem !important;

          .p-tree-toggler {
            display: none;
          }
        }
      }

      .p-tree-toggler:focus {
        box-shadow: none !important;
      }

      .p-tree-toggler:hover {
        background: var(--bs-white) !important;
      }

      .p-treenode-content.p-treenode-selectable:hover {
        background: var(--bs-form-row-highlight-background-color);
      }

      .p-highlight {
        background: var(--bs-form-row-highlight-background-color) !important;

        .sublabel {
          color: var(--bs-gray);
        }
      }

      .p-treenode-children .p-treenode-content.p-treenode-selectable:hover,
      .flat-tree .p-treenode-content.p-treenode-selectable:hover {
        background: var(--bs-tertiary-bg);
      }

      .cursor-pointer {
        cursor: pointer;
      }

      .p-treenode-children {
        padding-inline-start: 0px;

        .p-treenode-selectable {
          padding-inline-start: 2rem !important;
        }

        .p-tree-toggler {
          display: none;
        }
      }
    }
  `,
  imports: [
    TreeModule,
    StandardLineClampComponent,
    StandardSkeletonComponent,
    StandardCollapseIconComponent,
    WorkspaceListTreeItemComponent,
    StandardPageBlockingSpinnerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspaceListTreeComponent
  implements IHaveChildDialogRefs, OnDestroy
{
  dropdownType = "dropdown";
  contextMenuType = "contextMenu";

  activeFilterCode = input.required<WorkspaceFilterCode>();
  activeGroupCode = input.required<WorkspaceListGroupCode>();
  transitionState = model.required<TransitionState>();
  loading = model.required<boolean>();
  tree = model.required<WorkspaceTreeNode[]>();
  aptVersion = input.required<APTVersionType>();
  isSupportUser = input.required<boolean>();
  workspaceListContentWrapper = input.required<HTMLElement>();
  stickyParentNodes = input<boolean>(true);
  height = input<100 | "auto">(100);

  readonly onSelectionUpdated = output<WorkspaceTreeNode[]>();
  readonly navToWorkspace = output<WorkspaceTreeNode>();
  readonly refreshEngagements = output();

  private focusService = inject(FocusService);
  private workspaceListService = inject(WorkspaceListService);
  private favoriteEngagementWatcher = inject(FavoriteEngagementWatcherService);
  private dialogService = inject(DialogService);
  private snapshotManagementService = inject(SnapshotManagementService);
  private aptContext = inject(APTContext);
  private modalDialogService = inject(ModalDialogService);
  private keyboardShortcutService = inject(KeyboardShortcutService);

  constructor() {
    this.keyboardShortcutService.register([
      [
        "Delete",
        async (event) => {
          // Ignore if an input is focused
          if (isInputFocused()) {
            return;
          }

          //prevent Delete key from deleting workspace when workspace properties is open
          if (
            this.childDialogRefs().length === 0 &&
            !this.workspaceListService.isWorkSpacePropertiesOpen()
          ) {
            await this.removeWorkspace();
            // Prevent any other shortcuts with same key from handling this event
            event.preventDefault();
            event.stopPropagation();
          }
        },
      ],
    ]);
  }

  canPin = signal<boolean>(true);
  canCopy = signal<boolean>(false);
  canUnpin = signal<boolean>(true);
  canClone = signal<boolean>(false);
  canCopyId = signal<boolean>(false);
  canRemove = signal<boolean>(false);
  canRestore = signal<boolean>(false);
  canUnarchive = signal<boolean>(false);
  canRollForward = signal<boolean>(false);
  canViewProperties = signal<boolean>(false);
  canManageSnapshots = signal<boolean>(false);
  areCrossBorderOptionsVisible = signal<boolean>(false);

  contextMenuLoading = signal<boolean>(false);
  engagementId = signal<string | undefined>(undefined);
  selectedEngagements = signal<WorkspaceTreeNode[]>([]);
  childDialogRefs = signal<DynamicDialogRef<unknown>[]>([]);
  lastSelectedParentNode = signal<TreeNode | undefined>(undefined);

  treeItems = viewChildren<WorkspaceListTreeItemComponent>("treeItem");
  primengTreeRef =
    viewChild<TreeComponent<WorkspaceTreeNodeData>>("primengTree");

  selectedEngagementsChangeEffect = effect(() => {
    this.onSelectionUpdated.emit(this.selectedEngagements());
  });

  readonly parentItemSize = 32.19;
  readonly stickyParentItemSize = this.parentItemSize - 5.6;
  readonly virtualScrollItemSize: { parent: number; child: number } = {
    parent: this.parentItemSize,
    child: this.aptContext.isOfflineWebMode ? 81.87 : 51.09,
  };

  canDeleteEngagementSnapshots = computed<boolean>(() => {
    return this.aptContext.isOfflineAdmin() && this.aptContext.isOfflineWebMode;
  });

  async openCommonMessageDialog(title: string, message: string) {
    const SAVING_CLOSE_CONFIG = {
      title: title,
      message: message,
      confirmText: APTCodes.getMessage("OkButton"),
      confirmVariant: "primary",
      variant: "blocking",
    } satisfies BlockingConfirmationDialogRequest;
    await this.modalDialogService.showConfirmationDialog(SAVING_CLOSE_CONFIG);
  }

  unarchiveMenuItem = computed<IStandardButtonDropdownItem<WorkspaceTreeNode>>(
    () => {
      return {
        label: $localize`Unarchive`,
        keyboardShortcut: true,
        command: async (context) => {
          invariant(context?.data.engagementId);

          this.aptContext.setSupportUserSelectedMemberFirmId(
            `${context.data.memberFirmId}`,
          );
          await this.workspaceListService.unarchiveWorkspace(
            context.data.engagementId,
            context.data.memberFirmId,
          );
          this.aptContext.resetSupportUserSelectedMemberFirmId();

          // refresh the list
          this.refreshEngagements.emit();
          this.selectedEngagements.set([]);
        },
        disabled: !this.canUnarchive(),
      };
    },
  );

  copyMenuItem = computed<IStandardButtonDropdownItem<WorkspaceTreeNode>>(
    () => {
      return {
        label: $localize`Copy`,
        info: $localize`The copy option is to be used to create a new workspace for the same entity in the same period`,
        keyboardShortcut: true,
        command: async (context) => {
          invariant(context?.data.engagementId);
          if (!(await this.isCopyCloneRollforwardAllowed(context, "copy"))) {
            return;
          }
          this.engagementId.set(context.data.engagementId);
          await this.showCopyCloneOrRollForwardWorkspace(EngagementOption.Copy);
        },
        disabled: !this.canCopy() || this.areCrossBorderOptionsVisible(),
      };
    },
  );

  copyCrossBorderMenuItem = computed<
    IStandardButtonDropdownItem<WorkspaceTreeNode>
  >(() => {
    return {
      label: $localize`Copy cross border`,
      info: $localize`The cross border copy option is to be used to create a new workspace for the same entity for the same period. This process may take some time as the workspace is being copied and moved from the local firm environment on which it was created to your local firm environment. See help for further details.`,
      keyboardShortcut: true,
      command: async (context) => {
        invariant(context?.data.engagementId);
        this.engagementId.set(context.data.engagementId);
        await this.showCopyCloneOrRollForwardWorkspace(EngagementOption.Copy);
      },
      disabled: !this.canCopy(),
    };
  });

  cloneMenuItem = computed<IStandardButtonDropdownItem<WorkspaceTreeNode>>(
    () => {
      return {
        label: $localize`Clone`,
        info: $localize`The clone option is to be used to create a new workspace for a similar but not the same entity`,
        keyboardShortcut: true,
        command: async (context) => {
          invariant(context?.data.engagementId);
          if (!(await this.isCopyCloneRollforwardAllowed(context, "clone"))) {
            return;
          }
          this.engagementId.set(context.data.engagementId);
          await this.showCopyCloneOrRollForwardWorkspace(
            EngagementOption.Clone,
          );
        },
        disabled: !this.canClone() || this.areCrossBorderOptionsVisible(),
      };
    },
  );

  cloneCrossBorderMenuItem = computed<
    IStandardButtonDropdownItem<WorkspaceTreeNode>
  >(() => {
    return {
      label: $localize`Clone cross border`,
      info: $localize`The cross border clone option is to be used to create a new workspace for a similar but not the same entity. This process may take some time as the workspace is being cloned and moved from the local firm environment on which it was created to your local firm environment. See help for further details.`,
      keyboardShortcut: true,
      command: async (context) => {
        invariant(context?.data.engagementId);
        this.engagementId.set(context.data.engagementId);
        await this.showCopyCloneOrRollForwardWorkspace(EngagementOption.Clone);
      },
      disabled: !this.canClone(),
    };
  });

  private async isCopyCloneRollforwardAllowed(
    node: WorkspaceTreeNode,
    action: "copy" | "clone" | "rollforward",
  ): Promise<boolean> {
    const data = node.data;
    invariant(data.engagementId !== undefined);
    invariant(data.memberFirmId !== undefined);
    invariant(data.libraryVersion !== undefined);
    const [whoAmI, isRollForwardEnabled] = await Promise.all([
      this.workspaceListService.getWhoAmI(data.engagementId, data.memberFirmId),
      this.workspaceListService.getIsRollForwardEnabled(
        data.engagementId,
        data.libraryVersion,
        data.memberFirmId,
      ),
    ]);
    const role = whoAmI?.engagementRole ?? EngagementRole.None;

    const isGroupAdmin = this.workspaceListService.isGroupAdminRole(role);
    const isSupportUser = this.isSupportUser();
    const status = data.status ?? TeamMemberStatus.None;
    const state = data.state ?? EngagementState.None;

    const isDeactivated =
      status === TeamMemberStatus.Deactivated ||
      status === TeamMemberStatus.InactiveDeactivated;

    const isArchived =
      state === EngagementState.Archived || state === EngagementState.Archiving;

    const isCrossBorder = whoAmI.memberFirmId !== data.memberFirmId;
    let allowed = false;

    if (action === "copy") {
      allowed = isGroupAdmin && !isSupportUser && !isDeactivated;
    } else if (action === "clone") {
      allowed = isGroupAdmin && !isSupportUser && !isDeactivated && !isArchived;
    } else if (action === "rollforward") {
      allowed =
        isGroupAdmin &&
        !isSupportUser &&
        !isDeactivated &&
        isRollForwardEnabled &&
        !isCrossBorder;
    }

    if (!allowed) {
      this.transitionState.set("idle");
      await this.openCommonMessageDialog(
        APTCodes.getMessage("WarningDialogHeader"),
        APTCodes.getMessage("Unauthorized_0003"),
      );
      return false;
    }
    return true;
  }

  rollforwardMenuItem = computed<
    IStandardButtonDropdownItem<WorkspaceTreeNode>
  >(() => {
    return {
      label: $localize`Rollforward`,
      info: $localize`The rollforward option is to be used to create a new workspace for the same entity for the next period`,
      keyboardShortcut: true,
      command: async (context) => {
        invariant(context?.data.engagementId);
        if (
          !(await this.isCopyCloneRollforwardAllowed(context, "rollforward"))
        ) {
          return;
        }
        this.engagementId.set(context.data.engagementId);
        await this.showCopyCloneOrRollForwardWorkspace(
          EngagementOption.RollForward,
        );
      },
      disabled: !this.canRollForward(),
    };
  });

  contextMenuItems = computed<IStandardButtonDropdownItem<WorkspaceTreeNode>[]>(
    () => {
      const favoriteEngagementIds =
        this.favoriteEngagementWatcher.favoriteEngagementIds();

      const topMenuItems: IStandardButtonDropdownItem<WorkspaceTreeNode>[] = [
        {
          label: $localize`View workspace properties`,
          keyboardShortcut: true,
          command: async (context) => {
            invariant(context?.data.engagementId);
            this.aptContext.setSupportUserSelectedMemberFirmId(
              `${context.data.memberFirmId}`,
            );
            this.engagementId.set(context.data.engagementId);
            await this.showCopyCloneOrRollForwardWorkspace(
              EngagementOption.Edit,
            );
            this.aptContext.resetSupportUserSelectedMemberFirmId();
          },
          disabled: !this.canViewProperties(),
        },
        {
          label: $localize`Copy Engagement ID`,
          keyboardShortcut: true,
          command: async (context) => {
            invariant(context?.data.engagementId);
            await navigator.clipboard.writeText(context.data.engagementId);
          },
          disabled: !this.canCopyId(),
        },
        {
          type: "divider",
        },
      ];

      const bottomMenuItems: IStandardButtonDropdownItem<WorkspaceTreeNode>[] =
        [
          {
            label: $localize`Remove workspace`,
            keyboardShortcut: true,
            disabled: !this.canRemove(),
            command: async () => {
              await this.removeWorkspace();
            },
          },
          ...(this.canDeleteEngagementSnapshots()
            ? [
                {
                  label: $localize`Delete engagement snapshot`,
                  keyboardShortcut: true,
                  command: async (context: WorkspaceTreeNode | undefined) => {
                    invariant(context?.data.engagementId);
                    await this.deleteEngagementSnapshot(
                      context.data.engagementId,
                    );
                  },
                },
              ]
            : []),
          {
            label: $localize`Restore to list`,
            keyboardShortcut: true,
            disabled: !this.canRestore(),
            command: async () => {
              await this.restoreWorkspace();
            },
          },
          (context) => {
            const engagementId = context?.data.engagementId;
            invariant(engagementId);
            const isFavorite = favoriteEngagementIds.includes(engagementId);
            const isClassic = this.aptVersion() === APTVersion.Classic;

            if (isFavorite) {
              return {
                label: $localize`Unpin from top`,
                icon: faStar,
                keyboardShortcut: true,
                command: () => {
                  let isInactive = true;
                  this.selectedEngagements().forEach((node) => {
                    if (node.data.engagementId) {
                      this.favoriteEngagementWatcher.removeFavoriteEngagement(
                        node.data.engagementId,
                      );
                    }
                    if (
                      node.data.status !== TeamMemberStatus.Inactive &&
                      node.data.status !== TeamMemberStatus.InvitedInactive
                    ) {
                      isInactive = false;
                    }
                  });
                  this.selectedEngagements.set([]);
                  this.canPin.set(!isClassic && !isInactive);
                },
                disabled: !this.canUnpin(),
              };
            }

            return {
              label: $localize`Pin to top`,
              icon: faStarFilled,
              keyboardShortcut: true,
              command: () => {
                this.selectedEngagements().forEach((node) => {
                  if (node.data.engagementId) {
                    this.favoriteEngagementWatcher.addFavoriteEngagement(
                      node.data.engagementId,
                    );
                  }
                });
                this.selectedEngagements.set([]);
                this.canUnpin.set(!isClassic);
              },
              disabled: !this.canPin(),
            };
          },
        ];

      if (this.snapshotManagementService.canViewEngagementSnapshots()) {
        bottomMenuItems.unshift(
          {
            label: $localize`Manage engagement snapshot`,
            keyboardShortcut: true,
            command: async (ctx) => {
              invariant(ctx?.data.engagementId);
              if (ctx.data.memberFirmId == this.aptContext.userMemberFirmId()) {
                this.snapshotManagementService.loadEngagementSnapshotScreen(
                  ctx.data.engagementId,
                  ctx.label || "",
                );
              } else {
                const config: StandardConfirmationDialogRequest = {
                  title: $localize`Cross border engagement`,
                  message: $localize`Engagement snapshots cannot be managed for cross border engagements.`,
                  confirmText: $localize`OK`,
                  confirmVariant: "primary",
                  variant: "blocking",
                };
                void this.modalDialogService.showConfirmationDialog(config);
              }
            },
            disabled: !this.canManageSnapshots(),
          },
          {
            type: "divider",
          },
        );
      }

      const copyCloneRollforwardMenuItems: IStandardButtonDropdownItem<WorkspaceTreeNode>[] =
        [this.copyMenuItem()];
      if (this.areCrossBorderOptionsVisible()) {
        copyCloneRollforwardMenuItems.push(this.copyCrossBorderMenuItem());
        copyCloneRollforwardMenuItems.push(this.cloneMenuItem());
        copyCloneRollforwardMenuItems.push(this.cloneCrossBorderMenuItem());
      } else {
        copyCloneRollforwardMenuItems.push(this.cloneMenuItem());
      }
      copyCloneRollforwardMenuItems.push(this.rollforwardMenuItem());

      if (this.isSupportUser()) {
        return [
          ...topMenuItems,
          ...copyCloneRollforwardMenuItems,
          this.unarchiveMenuItem(),
          ...bottomMenuItems,
        ];
      } else {
        return [
          ...topMenuItems,
          ...copyCloneRollforwardMenuItems,
          {
            type: "divider",
          },
          ...bottomMenuItems,
        ];
      }
    },
  );

  ngOnDestroy(): void {
    this.childDialogRefs().forEach((ref) => {
      ref.close();
    });
  }

  async showCopyCloneOrRollForwardWorkspace(
    engagementOption: EngagementOption,
  ) {
    const workspaceListContentWrapper = this.workspaceListContentWrapper();
    const el =
      workspaceListContentWrapper.parentElement?.parentElement ?? undefined;

    const engagementId = this.workspaceListService.selectedEngagementId();
    if (engagementId) {
      const res =
        await this.workspaceListService.showCopyCloneOrRollForwardWorkspace(
          engagementOption,
          engagementId,
          false,
          el,
          this.childDialogRefs,
          this.workspaceListService.selectedEngagementMemberFirmId(),
        );
      if (res) {
        this.refreshEngagements.emit();
        this.selectedEngagements.set([]);
      } else if (el) {
        this.focusService.focusFirst(el);
      }
    }
  }

  onWorkspaceDoubleClicked(node: WorkspaceTreeNode) {
    this.navToWorkspace.emit(node);
  }

  onParentNodeClicked(node: TreeNode) {
    node.expanded = !node.expanded;
    this.updateSerializedValue();

    if (node.label) {
      this.workspaceListService.setListItemState(node.label, node.expanded);
    }
  }

  updateSerializedValue() {
    this.primengTreeRef()?.updateSerializedValue();
  }

  private prevSelection:
    | TreeNode<WorkspaceTreeNodeData>
    | readonly TreeNode<WorkspaceTreeNodeData>[]
    | undefined;
  async handleSelectionChange(
    node:
      | TreeNode<WorkspaceTreeNodeData>
      | readonly TreeNode<WorkspaceTreeNodeData>[]
      | undefined,
  ) {
    // Avoid extra work if the selection hasn't changed
    if (isStrictEqualish(node, this.prevSelection)) {
      return;
    }

    this.prevSelection = node;

    if (!node) {
      this.selectedEngagements.set([]);
    } else if (Array.isArray(node)) {
      this.selectedEngagements.set(node as WorkspaceTreeNode[]);
      if (this.selectedEngagements().length === 1) {
        this.workspaceListService.selectedEngagementId.set(
          this.selectedEngagements()[0]?.data.engagementId ?? "",
        );
        this.workspaceListService.selectedEngagementMemberFirmId.set(
          this.selectedEngagements()[0]?.data.memberFirmId ?? 0,
        );
        if (this.workspaceListService.engagementDetailsRef) {
          const dialogInstance = this.dialogService.dialogComponentRefMap.get(
            this.workspaceListService.engagementDetailsRef,
          )?.instance;
          if (dialogInstance) {
            const componentInstance = dialogInstance.componentRef
              ?.instance as EngagementDetailsComponent;
            componentInstance["config"] = {
              data: {
                engagementId: this.workspaceListService.selectedEngagementId(),
                engagementOption: EngagementOption.Edit,
                fromSearchPanel: false,
                engagementMemberFirmId:
                  this.selectedEngagements()[0]?.data.memberFirmId,
              },
            } satisfies DynamicDialogConfig<ICopyCloneAndRollforwardModalProps>;
            await componentInstance.ngOnInit();
          }
        }
      }
    } else {
      this.selectedEngagements.set([node as WorkspaceTreeNode]);
    }

    await this.updateMenuOptions();
  }

  async onRestoreRemoteClicked(node: WorkspaceTreeNode) {
    this.selectedEngagements.set([node]);
    await this.restoreRemoteWorkspace(node);
  }

  async onMenuHover(event: MenuHoverEvent) {
    // pre-fetch+cache related engagement details
    await this.getAndCacheEngagementMemberData(event.node.data);
  }

  async onMenuToggled(event: MenuToggleEvent) {
    if (!event.wasOpened) {
      return;
    }

    // If the node is not already selected,
    // we mark it as the only selected node
    // if it's already part of the selection, do nothing special
    if (
      this.selectedEngagements().length === 0 ||
      !this.selectedEngagements().includes(event.node)
    ) {
      this.selectedEngagements.set([event.node]);
      this.workspaceListService.selectedEngagementId.set(
        this.selectedEngagements()[0]?.data.engagementId ?? "",
      );
      this.workspaceListService.selectedEngagementMemberFirmId.set(
        this.selectedEngagements()[0]?.data.memberFirmId ?? 0,
      );
    }

    this.contextMenuLoading.set(true);
    await this.updateMenuStates(event.node, event.menu, event.wasOpened);
    await this.updateMenuOptions();
  }

  async updateMenuStates(
    node: WorkspaceTreeNode,
    menu:
      | StandardButtonDropdownActionsComponent<WorkspaceTreeNode>
      | StandardContextMenuComponent<WorkspaceTreeNode>,
    wasOpened: boolean,
  ) {
    invariant(node.data.engagementId);
    if (wasOpened) {
      for (const treeItem of this.treeItems()) {
        const contextMenu = treeItem.contextMenu();
        if (contextMenu && contextMenu !== menu) {
          await contextMenu.close();
        }

        const dropdownMenu = treeItem.dropdownMenu();
        if (dropdownMenu !== menu) {
          await dropdownMenu.close();
        }
      }
    }
  }

  #cachedEngagementMemberData = new Map<
    string,
    | [Date, EngagementTeamMember, boolean]
    | Promise<[Date, EngagementTeamMember, boolean]>
  >();
  async getAndCacheEngagementMemberData(data: WorkspaceTreeNodeData) {
    invariant(data.engagementId !== undefined);
    invariant(data.memberFirmId !== undefined);
    invariant(data.libraryVersion !== undefined);

    const key = `${data.engagementId}_${data.memberFirmId}`;
    const found = this.#cachedEngagementMemberData.get(key);
    if (found) {
      const [lastUpdate, teamMember, isRollForward] = await found;
      if (Date.now() - lastUpdate.getTime() < CACHE_ENGAGEMENT_MEMBER_DATA_MS) {
        return [teamMember, isRollForward] as const;
      }
    }

    const fetcher = Promise.all([
      // eslint-disable-next-line
      new Date(),
      this.workspaceListService.getWhoAmI(data.engagementId, data.memberFirmId),
      this.workspaceListService.getIsRollForwardEnabled(
        data.engagementId,
        data.libraryVersion,
        data.memberFirmId,
      ),
    ]);
    this.#cachedEngagementMemberData.set(key, fetcher);

    const res = await fetcher;
    this.#cachedEngagementMemberData.set(key, res);
    const [_, ...rest] = res;
    return rest;
  }

  #lastMenuEngagementIdsCsv: string | undefined;
  async updateMenuOptions() {
    // Skip re-processing if we've already handled this one
    const menuEngagementIdsCsv = this.selectedEngagements()
      .map((engagement) => engagement.data.engagementId)
      .join(",");
    if (menuEngagementIdsCsv === this.#lastMenuEngagementIdsCsv) {
      this.contextMenuLoading.set(false);
      return;
    }
    this.#lastMenuEngagementIdsCsv = menuEngagementIdsCsv;

    const isClassic = this.aptVersion() === APTVersion.Classic;
    const isSupportUser = this.isSupportUser();
    const isOfflineWebMode = this.aptContext.isOfflineWebMode;

    if (this.selectedEngagements().length === 0) {
      this.canRemove.set(false);
      this.canRestore.set(false);
      this.canPin.set(false);
      this.canUnpin.set(false);
      this.canClone.set(false);
      this.canCopy.set(false);
      this.canCopyId.set(false);
      this.canRollForward.set(false);
      this.canViewProperties.set(false);
      this.canManageSnapshots.set(false);
      this.areCrossBorderOptionsVisible.set(false);
      this.contextMenuLoading.set(false);
      return;
    } else if (this.selectedEngagements().length === 1) {
      const selectedEngagement = this.selectedEngagements()[0];
      invariant(selectedEngagement);
      invariant(selectedEngagement.data.engagementId !== undefined);
      invariant(selectedEngagement.data.memberFirmId !== undefined);
      invariant(selectedEngagement.data.libraryVersion !== undefined);

      const [teamMember, isRollForward] =
        await this.getAndCacheEngagementMemberData(selectedEngagement.data);
      const isCrossBorder =
        teamMember.memberFirmId !== selectedEngagement.data.memberFirmId;
      this.areCrossBorderOptionsVisible.set(isCrossBorder);

      const isGroupAdmin = this.workspaceListService.isGroupAdminRole(
        selectedEngagement.data.role ?? EngagementRole.None,
      );
      const status = selectedEngagement.data.status ?? TeamMemberStatus.None;
      const isDeactivated =
        status === TeamMemberStatus.Deactivated ||
        status === TeamMemberStatus.InactiveDeactivated;
      const state = selectedEngagement.data.state ?? EngagementState.None;
      const isArchived =
        state === EngagementState.Archived ||
        state === EngagementState.Archiving;
      const isActive =
        status === TeamMemberStatus.Active ||
        status === TeamMemberStatus.Invited ||
        isSupportUser;
      const isInRemoteStorage =
        this.workspaceListService.isRemoteStorageState(state);
      const canCopyAndClone =
        !isClassic &&
        !isInRemoteStorage &&
        isGroupAdmin &&
        !isDeactivated &&
        !isSupportUser &&
        !isOfflineWebMode;

      this.canViewProperties.set(isActive);
      this.canCopyId.set(true);
      this.canCopy.set(canCopyAndClone);
      this.canClone.set(canCopyAndClone && !isArchived);
      this.canManageSnapshots.set(!isCrossBorder && !isInRemoteStorage);

      this.canRollForward.set(
        this.canCopy() && isRollForward && !isCrossBorder,
      );
      this.canUnarchive.set(isSupportUser && isArchived);
    } else if (this.selectedEngagements().length > 1) {
      this.canClone.set(false);
      this.canCopy.set(false);
      this.canCopyId.set(false);
      this.canRollForward.set(false);
      this.canViewProperties.set(false);
      this.canManageSnapshots.set(false);
      this.areCrossBorderOptionsVisible.set(false);
    }

    let isRemoteStorage = false;
    let hasFavoritesSelected = false;
    let hasNonFavoritesSelected = false;
    let isActive = true;
    let isInactive = true;
    const favoriteIds = this.favoriteEngagementWatcher.favoriteEngagementIds();
    for (const node of this.selectedEngagements()) {
      invariant(node.data.engagementId);
      invariant(node.data.state);
      invariant(node.data.status);
      if (favoriteIds.includes(node.data.engagementId)) {
        hasFavoritesSelected = true;
      } else {
        hasNonFavoritesSelected = true;
      }

      if (this.workspaceListService.isRemoteStorageState(node.data.state)) {
        isRemoteStorage = true;
      }

      if (
        node.data.status !== TeamMemberStatus.Active &&
        node.data.status !== TeamMemberStatus.Invited
      ) {
        isActive = false;
      }

      if (
        node.data.status !== TeamMemberStatus.Inactive &&
        node.data.status !== TeamMemberStatus.InvitedInactive
      ) {
        isInactive = false;
      }
    }

    this.canRestore.set(!isRemoteStorage && !isActive && !isSupportUser);

    this.canRemove.set(
      !isRemoteStorage && !isInactive && !isSupportUser && !isOfflineWebMode,
    );
    const numFavoritesAfterAdd =
      this.favoriteEngagementWatcher.favoriteEngagementIds().length +
      this.selectedEngagements().length;

    this.canPin.set(
      !isClassic &&
        numFavoritesAfterAdd <= 5 &&
        !hasFavoritesSelected &&
        hasNonFavoritesSelected &&
        !isInactive &&
        !isRemoteStorage,
    );

    this.canUnpin.set(
      !isClassic && !hasNonFavoritesSelected && hasFavoritesSelected,
    );
    this.contextMenuLoading.set(false);
  }

  onNodeStateChange(event: TreeNodeExpandEvent | TreeNodeCollapseEvent) {
    if (event.node.label && event.node.expanded !== undefined) {
      this.workspaceListService.setListItemState(
        event.node.label,
        event.node.expanded,
      );
    }
  }

  // Fix for p-tree node content not being tabbable
  onTreeKeydown(event: KeyboardEvent) {
    const target = event.target as HTMLElement | undefined;
    const treeContent = target?.querySelector(".p-treenode-content") as
      | HTMLElement
      | undefined;
    if (!treeContent) {
      return;
    }

    if (event.key === "Tab") {
      const insideActionsBtn = treeContent.querySelector(
        "standard-button-dropdown",
      );
      if (insideActionsBtn) {
        event.preventDefault();
        this.focusService.focusFirst(insideActionsBtn as HTMLElement);
      }

      const treeNode = target?.closest("p-treenode");
      const isLastNode = treeNode?.nextElementSibling === null;

      if (isLastNode) {
        const treeElement = target?.closest("app-workspace-list-tree");
        const isFavoritesTree =
          treeElement?.classList.contains("favorites-tree");
        const isStickyNode = treeNode.matches(
          ".p-tree-sticky-container p-treenode",
        );

        if (!isFavoritesTree && !isStickyNode) {
          const workspaceList = target?.closest("app-workspace-list");
          if (workspaceList) {
            this.focusService.focusFirst(workspaceList as HTMLElement);
          }
        }
      }
    }

    if (event.key === "Enter") {
      if (this.selectedEngagements().length === 1) {
        const selectedNode = this.selectedEngagements()[0];
        if (selectedNode && selectedNode.children === undefined) {
          this.onWorkspaceDoubleClicked(selectedNode);
        }
      }
    }
  }

  async removeWorkspace(): Promise<void> {
    this.loading.set(true);

    const removeResult = await this.workspaceListService.removeWorkspace(
      this.selectedEngagements(),
    );

    if (removeResult) {
      this.#lastMenuEngagementIdsCsv = undefined;
      this.refreshEngagements.emit();
      this.selectedEngagements.set([]);
    } else {
      this.loading.set(false);
    }
  }

  async deleteEngagementSnapshot(
    deleteEngagementSnapshot: string,
  ): Promise<void> {
    this.loading.set(true);

    const deleteResult =
      await this.workspaceListService.deleteEngagementSnapshot(
        deleteEngagementSnapshot,
      );

    if (deleteResult) {
      this.refreshEngagements.emit();
      this.selectedEngagements.set([]);
    } else {
      this.loading.set(false);
    }
  }

  async restoreWorkspace() {
    this.loading.set(true);
    const restoreResult = await this.workspaceListService.restoreWorkspace(
      this.selectedEngagements(),
    );

    if (restoreResult) {
      this.#lastMenuEngagementIdsCsv = undefined;
      this.refreshEngagements.emit();
      this.selectedEngagements.set([]);
    } else {
      this.loading.set(false);
    }
  }

  async restoreRemoteWorkspace(node: WorkspaceTreeNode) {
    const restoreResult =
      await this.workspaceListService.restoreWorkspaceFromRemote(node);

    this.loading.set(true);
    if (restoreResult) {
      this.refreshEngagements.emit();
    }
    this.loading.set(false);
  }

  // Optimize tree rendering by tracking nodes by unique identifier
  trackTreeNode(index: number, item: SerializedNode<WorkspaceTreeNodeData>) {
    return item.node.data!.engagementId;
  }
}
